import { COLUMNS, modalityName, type Modality, type WinnerState } from '@bingo/shared';
import { useEffect, useRef } from 'react';
import { runConfetti } from '../lib/confetti.js';

/**
 * Comemoração do ganhador — mesma estrutura do protótipo (`.celebrate`):
 * a palavra BINGO letra por letra com as cores das colunas, foto redonda com
 * aro dourado e os metadados da vitória. Sem botão: no telão o fechamento é
 * decidido pelo /controle, não por quem está assistindo.
 */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

interface CelebrationProps {
  winner: WinnerState;
  roundNumber: number;
  modality: Modality;
  prizeText: string | null;
}

export function Celebration({ winner, roundNumber, modality, prizeText }: CelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const visibleRef = useRef(true);

  useEffect(() => {
    visibleRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const stop = runConfetti(canvas, () => visibleRef.current);

    return () => {
      visibleRef.current = false;
      stop();
    };
  }, [winner.id]);

  const meta = [
    `Rodada ${roundNumber}`,
    modalityName(modality),
    winner.cardNumber ? `Cartela ${winner.cardNumber}` : null,
    `${winner.ballsAtWin} bolas`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="celebrate" id="cel">
      <canvas id="confetti" ref={canvasRef} />
      <div className="cel-inner">
        <div className="bingo-word" aria-label="Bingo">
          {['B', 'I', 'N', 'G', 'O'].map((letter, index) => (
            <span
              key={letter}
              style={
                {
                  '--c': COLUMNS[index]?.color,
                  '--d': index,
                } as React.CSSProperties
              }
            >
              {letter}
            </span>
          ))}
          <span style={{ '--c': 'var(--gold)', '--d': 5 } as React.CSSProperties}>!</span>
        </div>

        <div className="winner">
          <div className="big-av" id="cAv">
            {winner.photoUrl ? <img src={winner.photoUrl} alt="" /> : initials(winner.name) || '★'}
          </div>
          <div className="wtext">
            <div className="wname" id="cName">
              {winner.name}
            </div>
            <div className="wmeta" id="cMeta">
              {meta}
            </div>
            <div className="wprize" id="cPrize">
              {prizeText ? `Prêmio: ${prizeText}` : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
